/**
 * pages/UserEditorPage.tsx
 * Author: Sean Johnson <sean@scj.net>
 * Purpose: Per-user permission editor — auth.scj.net/settings/users/:sub
 * Last Updated: 2026-05-31
 * Version: 1.0.0
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi, type AdminUser, ALL_APP_GROUPS, APP_LABELS, APP_COLORS } from '../hooks/useAdmin'

export function UserEditorPage() {
  const { sub } = useParams<{ sub: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<AdminUser | null>(null)
  const [groups, setGroups] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (!sub) return
    loadUser()
  }, [sub])

  async function loadUser() {
    try {
      setLoading(true)
      const u = await adminApi.getUser(sub!)
      setUser(u)
      setGroups(u.groups)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function toggleGroup(group: string) {
    setGroups(prev =>
      prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]
    )
  }

  async function handleSaveGroups() {
    if (!sub) return
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      await adminApi.updateGroups(sub, groups)
      setSuccess('Permissions saved')
      await loadUser()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleEnabled() {
    if (!user || !sub) return
    setToggling(true)
    setError('')
    setSuccess('')
    try {
      await adminApi.updateUser(sub, !user.enabled)
      setSuccess(`User ${user.enabled ? 'disabled' : 'enabled'}`)
      await loadUser()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setToggling(false)
    }
  }

  if (loading) {
    return (
      <div className="settings-container">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="settings-container">
        <div className="auth-error">User not found</div>
        <button onClick={() => navigate('/settings')} className="auth-link text-sm mt-4">← Back</button>
      </div>
    )
  }

  const hasChanges = JSON.stringify([...groups].sort()) !== JSON.stringify([...user.groups].sort())

  return (
    <div className="settings-container max-w-xl">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/settings')}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          ← Users
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm text-gray-700 font-medium">{user.email}</span>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}

      {/* User info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Account</h2>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Email</span>
            <span className="font-medium text-gray-900">{user.email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Status</span>
            <span className={`font-medium ${user.enabled ? 'text-green-700' : 'text-gray-400'}`}>
              {user.status}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Created</span>
            <span className="text-gray-600">{new Date(user.created).toLocaleDateString()}</span>
          </div>
          <div className="pt-3 border-t border-gray-100">
            <button
              onClick={handleToggleEnabled}
              disabled={toggling}
              className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                user.enabled
                  ? 'bg-red-50 text-red-700 hover:bg-red-100'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              {toggling ? 'Updating…' : user.enabled ? 'Disable user' : 'Enable user'}
            </button>
          </div>
        </div>
      </div>

      {/* App permissions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-4">
        <h2 className="text-base font-semibold text-gray-900 mb-4">App Access</h2>
        <div className="space-y-2">
          {ALL_APP_GROUPS.map(group => {
            const active = groups.includes(group)
            return (
              <label
                key={group}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${APP_COLORS[group]}`}>
                    {APP_LABELS[group]}
                  </span>
                  <span className="text-sm text-gray-500">
                    {group.replace('app:', '')}.scj.net
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGroup(group)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </label>
            )
          })}
        </div>
      </div>

      {/* Admin group */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Admin</h2>
        <label className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${APP_COLORS['admin']}`}>
              admin
            </span>
            <span className="text-sm text-gray-500">Access to auth.scj.net/settings</span>
          </div>
          <input
            type="checkbox"
            checked={groups.includes('admin')}
            onChange={() => toggleGroup('admin')}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
        </label>
      </div>

      <button
        onClick={handleSaveGroups}
        disabled={saving || !hasChanges}
        className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? 'Saving…' : hasChanges ? 'Save Changes' : 'No Changes'}
      </button>
    </div>
  )
}
